import ldUniq from 'lodash/uniq';
import Button from '../../atoms/Button';
import Grid from '../../atoms/Grid';
import Notice from '../../atoms/Notice';
import Modal from '../../molecules/Modal';
import { Icon } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppState } from '../../../redux/modules/store';
import { storeFilterActions, modalConfirmActions } from '../../../redux/modules/Actions';
import { ModalConfirmState } from '../../../redux/reducers/ModalReducer';
import Textbox from '../../atoms/Textbox';
import { createFilter, deleteFilter, updateFilter } from '../../../redux/operations/StoreFilterList';
import useValidateFilterName from '../../../utils/CustomHooks/useValidateFilter';
import StoreFilterAgencyContainer from '../../molecules/StoreFilterAgency/StoreFilterAgencyContainer';
import { FilterItem, newFilterID } from '../../../redux/reducers/StoreFilterReducer';
import { useDragAndDrop } from '../../../utils/CustomHooks/useDragAndDropFilter';
import StoreFilterEmployee from '../../molecules/StoreFilterEmployee/StoreFilterContainer';
import { countActiveStores } from '../../../utils/StoreFilterList';
import { AgencyInfo } from '../../../redux/reducers/StoreSelectorReducer';
import { STORE_FILTER_SPOT_EVENT_MAPPING } from '../../../Common/index';
import { GASpotEventState } from '../../organisms/NegotiationCountList';

// Propsのインターフェース定義
interface StoreFilterManagementProps {
  isOpenModal: boolean;
  onCloseModal: (callback?: () => void, handleCancelWithCheck?: boolean) => void;
  isAgency?: boolean;
}

export const defaultItem: FilterItem = {
  filterID: '',
  filterName: '',
  ordcstmCdList: [],
  isActive: true,
  checked: false,
  sortOrder: 0,
  isExist: true,
};

// 差分比較関数（初期画面と現在の画面）
export function isFormChange(snapshotStr: string | null, currentStr: string | null): boolean {
  if (!snapshotStr) return false;
  return snapshotStr !== currentStr;
}

const StoreFilterManagement: React.FC<StoreFilterManagementProps> = ({ isOpenModal, onCloseModal, isAgency = false }) => {
  // モジュールに応じたイベントマッピングを取得
  const eventMapping = isAgency ? STORE_FILTER_SPOT_EVENT_MAPPING['AGENCY'] : STORE_FILTER_SPOT_EVENT_MAPPING['EMPLOYEE'];
  const [isCreatingFilter, setIsCreatingFilter] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { data, isDraft, msg, isLoading, isDraftOrder } = useSelector((state: AppState) => state.filterStore);
  const { groupAgencies } = useSelector((state: AppState) => state.storeSelector);
  const itemActive = useMemo(() => {
    const activeIndex = data.findIndex((item) => item.isActive);
    return activeIndex === -1 ? defaultItem : data[activeIndex];
  }, [data]);

  // スナップショット取得項目（項目が増えた場合はここに追記する）
  const snapshotKeys = (item: FilterItem) => ({
    filterName: item.filterName,
    ordcstmCdList: [...item.ordcstmCdList].sort(), // 配列は順番が変わると差分とみなされるためソートして比較
  });

  // スナップショット格納変数
  const snapshotStr = useRef<string | null>(null);

  // スナップショット生成関数
  const createSnapshotStr = (item: FilterItem) => {
    return JSON.stringify(snapshotKeys(item));
  };

  // スナップショット取得関数
  const updateSnapshot = (targetItem?: FilterItem) => {
    const snapshotDate = targetItem || itemActive;
    snapshotStr.current = createSnapshotStr(snapshotDate);
  };

  // useEffectを使用してDOMに直接GAイベントを設定
  useEffect(() => {
    if (!isOpenModal) return;

    // DOMの描画完了を待つために遅延させる
    const timer = setTimeout(() => {
      // 代理店か社員かに応じてマッピングを取得
      const mapping = isAgency ? STORE_FILTER_SPOT_EVENT_MAPPING['AGENCY'] : STORE_FILTER_SPOT_EVENT_MAPPING['EMPLOYEE'];

      // マッピングに含まれる各定義に対して処理
      Object.values(mapping).forEach(({ className, spotEvent }) => {
        if (spotEvent) {
          document.querySelector(`.${className}`)?.setAttribute('onclick', spotEvent);
        }
      });

      // バツボタン（Modal.Header内の閉じるボタン）へのイベント設定
      const closeMapping = mapping['close'];
      if (closeMapping?.spotEvent) {
        document.querySelector('.filter-modal-store .uc-modal__close-button')?.setAttribute('onclick', closeMapping.spotEvent);
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [isAgency, isOpenModal, itemActive]);

  // モーダルオープン時
  useEffect(() => {
    if (!isOpenModal) return;
    updateSnapshot();
  }, [isOpenModal]);

  const err = useValidateFilterName(itemActive, isCreatingFilter);
  const { handleDragEnd, handleDragEnter, handleDragOver, handleDragStart, handleDrageLeave, handleDrop } = useDragAndDrop(data);
  // 新規作成ボタン押下時
  const handleDraftCreateClick = () => {
    const payload: ModalConfirmState = {
      isLoading: false,
      isOpen: true,
      isDanger: true,
      title: '他の店舗フィルタを表示しますか？',
      type: '',
      msg: '保存していない変更内容は破棄されます。',
      primaryBtnText: 'このまま表示',
      callBack: () => {
        setIsCreatingFilter(true);
        dispatch(storeFilterActions.draftFilter.rollBack());
        dispatch(storeFilterActions.draftFilter.create());
      },
    };
    if (isFormChange(snapshotStr.current, createSnapshotStr(itemActive)) || isDraftOrder) return dispatch(modalConfirmActions.trigglerModal(payload));
    setIsCreatingFilter(true);
    dispatch(storeFilterActions.draftFilter.create());
  };

  // 他フィルタ選択時
  const handleListFilterItemClick = (item: FilterItem) => {
    if (item.filterID === itemActive.filterID) return;
    const payload: ModalConfirmState = {
      isLoading: false,
      isOpen: true,
      isDanger: true,
      title: '他の店舗フィルタを表示しますか？',
      type: '',
      msg: '保存していない変更内容は破棄されます。',
      primaryBtnText: 'このまま表示',
      callBack: () => {
        setIsCreatingFilter(false);
        dispatch(storeFilterActions.draftFilter.rollBack());
        dispatch(storeFilterActions.resetErr());
        dispatch(storeFilterActions.activeFilter(item));
        // フィルタ選択時スナップショットを更新
        updateSnapshot(item);
      },
    };
    if (item.filterID !== itemActive.filterID && (isFormChange(snapshotStr.current, createSnapshotStr(itemActive)) || isDraftOrder)) {
      dispatch(modalConfirmActions.trigglerModal(payload));
      return;
    }
    setIsCreatingFilter(false);
    dispatch(storeFilterActions.draftFilter.rollBack());
    dispatch(storeFilterActions.resetErr());
    dispatch(storeFilterActions.activeFilter(item));
    // フィルタ選択時スナップショットを更新
    updateSnapshot(item);
  };
  const handleFilterNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(storeFilterActions.draftFilter.update({ ...itemActive, filterName: e.target.value }));
  };
  const handleDeleteErrMsg = (item: ResultMessageDto) => {
    dispatch(storeFilterActions.deleteErr(item.resultMessageCd));
  };
  const handleDeleteFilter = () => {
    const payload: ModalConfirmState = {
      isLoading: false,
      isOpen: true,
      isDanger: true,
      title: 'このフィルタを削除しますか？',
      type: '',
      msg: '保存していない内容は失われます。',
      primaryBtnText: '削除',
      callBack: () => {
        if (itemActive.filterID !== newFilterID) {
          dispatch(deleteFilter(itemActive));

          // フィルタ削除時スナップショットを更新
          const nextData = data.filter((item) => item.filterID !== itemActive.filterID);
          const nextActiveItem = nextData[0] || defaultItem;

          updateSnapshot(nextActiveItem);
        }
      },
    };
    setIsCreatingFilter(false);
    dispatch(modalConfirmActions.trigglerModal(payload));
  };
  const handleUpdateFilter = () => {
    const payload: ModalConfirmState = {
      isLoading: false,
      isOpen: true,
      isDanger: false,
      title: '',
      type: '',
      msg: (
        <>
          店舗表示条件を保存します。
          <br />
          保存済みの店舗表示条件について編集している場合は上書き保存されます。
        </>
      ),
      primaryBtnText: '保存',
      callBack: () => {
        const { filterID } = itemActive;
        const storeList = isAgency ? itemActive.ordcstmCdList : ldUniq(itemActive.ordcstmCdList);
        if (filterID && filterID !== newFilterID) {
          dispatch(updateFilter({ ...itemActive, ordcstmCdList: storeList }));
          updateSnapshot();
          return;
        }

        dispatch(createFilter({ filterName: itemActive.filterName, ordcstmCdList: storeList }, isAgency));
        updateSnapshot();
      },
    };

    setIsCreatingFilter(false);
    dispatch(modalConfirmActions.trigglerModal(payload));
  };
  const handleCloseModal = () => {
    const callback = () => {
      setIsCreatingFilter(false);
    };
    const handleCancelWithCheck = isFormChange(snapshotStr.current, createSnapshotStr(itemActive)) || isDraftOrder;
    onCloseModal(callback, handleCancelWithCheck);
  };

  const isDisabledAddBtn = useMemo(() => isLoading, [isLoading]);
  const isDisableDeleteButton = useMemo(() => !data.length || itemActive.filterID === newFilterID, [data, itemActive]);
  const isDisabledUpdateBtn = useMemo(
    () => (itemActive.isExist && !(!err && (isDraft || isDraftOrder))) || itemActive.ordcstmCdList.length === 0,
    [err, isDraft, isDraftOrder, itemActive],
  );

  return (
    <Modal
      className='filter-modal filter-modal-store'
      open={isOpenModal}
      onClose={handleCloseModal}
      onBackdropClick={handleCloseModal}
      onEscapeKeyDown={handleCloseModal}
      surfaceClassName='u-mv-auto'>
      <Modal.Header data-testId='modal-filter-store-header'>店舗フィルタ</Modal.Header>
      <div className='uc-divider' />
      <Grid>
        <Grid.Row className='filter-modal__store-filter-container'>
          <Grid.Col
            col={3}
            style={{
              borderRight: 'solid 1px #e3dfdf',
              paddingLeft: 0,
            }}
            className='filter-modal__drilldown-item'>
            <Button
              className={`u-w5s btn-non-focus ${eventMapping['create'].className}`}
              type='transparent'
              height='s'
              leftIcon='add'
              label='新規フィルタ'
              width='full'
              data-testid-e2e='btn-add-filter-store'
              data-testid='btnAddFilterStore'
              style={{ justifyContent: 'start', paddingLeft: 10 }}
              disabled={isDisabledAddBtn}
              onClick={handleDraftCreateClick}
            />
            <StoreFilterList
              data={data}
              isAgency={isAgency}
              groupAgencies={groupAgencies}
              handleDragStart={handleDragStart}
              handleDragEnd={handleDragEnd}
              handleDragOver={handleDragOver}
              handleDragEnter={handleDragEnter}
              handleDrop={handleDrop}
              handleDrageLeave={handleDrageLeave}
              handleListFilterItemClick={handleListFilterItemClick}
            />
          </Grid.Col>
          <Grid.Col col={9} className='filter-modal__store-filter-type-container'>
            <Modal.Content className='u-p0'>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ paddingLeft: 5, minWidth: 200 }}>フィルタ名（15文字以内）：</div>
                <Textbox
                  style={{ border: 'none' }}
                  wrapperClass='filter-modal__textbox u-w-full'
                  width='l'
                  data-testid='filterNameInput'
                  value={itemActive.filterName}
                  onChange={handleFilterNameChange}
                />
              </div>

              {/* switch modal content */}
              <StoreFilterType isAgency={isAgency} />
            </Modal.Content>

            {/* error from api response display */}
            {msg.map((item) => (
              <Notice
                icon='error'
                type='error'
                isLong
                data-testid={`${item.resultMessageCd}`}
                key={item.resultMessageCd}
                onClickClose={() => {
                  handleDeleteErrMsg(item);
                }}>
                <>{item.resultMessage}</>
              </Notice>
            ))}
            {/* error from client validate input*/}
            <ErrorFromClient error={err} />

            {/* err from store is not exist */}
            <ErrorFromStore isExist={itemActive.isExist} />

            <div className='uc-divider' />

            <Modal.Actions style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Button
                className={`u-w5s btn-non-focus ${eventMapping['delete'].className}`}
                isDanger
                label='このフィルタを削除'
                type='transparent'
                data-testid-e2e='btn-delete-filter-store'
                data-testid='btnDeleteFilterStore'
                onClick={handleDeleteFilter}
                disabled={isDisableDeleteButton}
              />
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Typography style={{ display: 'inline-block', paddingRight: 5 }} variant='h4'>
                  {countActiveStores(itemActive.ordcstmCdList, isAgency, groupAgencies)}
                </Typography>
                <label className='uc-step__message uc-file-uploader__file-item-name u-text-middle'>件の取次店を選択中</label>
                <Button
                  type='secondary'
                  className={`btn-non-focus ${eventMapping['cancel'].className}`}
                  label='キャンセル'
                  onClick={handleCloseModal}
                  data-testid-e2e='btn-close-filter-store'
                  data-testid='btnCloseFilterStore'
                />
                <Button
                  disabled={isDisabledUpdateBtn}
                  className={`${eventMapping['save'].className}`}
                  label='フィルタを保存'
                  onClick={handleUpdateFilter}
                  data-testid-e2e='btn-update-filter-store'
                  data-testid='btnUpdateFilterStore'
                />
              </div>
            </Modal.Actions>
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </Modal>
  );
};

interface StoreFilterListProps {
  data: FilterItem[];
  isAgency: boolean;
  groupAgencies?: {
    [key: string]: AgencyInfo[];
  };
  gaSpotEvent?: {
    [key: string]: GASpotEventState;
  };
  handleDragStart: (e: React.DragEvent<HTMLElement>, index: number) => void;
  handleDragEnter: (e: React.DragEvent<HTMLElement>, index: number) => void;
  handleDragOver: (e: React.DragEvent<HTMLElement>) => void;
  handleDrageLeave: (e: React.DragEvent<HTMLElement>) => void;
  handleDragEnd: (e: React.DragEvent<HTMLElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLElement>) => void;
  handleListFilterItemClick: (item: FilterItem) => void;
}

export const StoreFilterList = ({
  data,
  isAgency,
  groupAgencies,
  handleDragStart,
  handleDragEnter,
  handleDragOver,
  handleDrageLeave,
  handleDragEnd,
  handleDrop,
  handleListFilterItemClick,
}: StoreFilterListProps) => {
  return (
    <ul
      className='list filter-modal__scroll-area'
      style={{ maxHeight: `calc(${isAgency ? '100vh' : '100svh'} - 240px)` }}
      data-testid={`filter-list`}>
      {data.map((item, index) => {
        const activeClassName = item.isActive ? `list__item-link--active` : '';
        return (
          <li
            className={`list__item-link ${activeClassName} list__item-link--has-right-icon list__item-link--has-left-icon`}
            data-testid={`filter-list-drag`}
            key={item.filterID}
            draggable
            onDragStart={(e) => {
              handleDragStart(e, index);
            }}
            onDragEnter={(e) => handleDragEnter(e, index)}
            onDragOver={handleDragOver}
            onDragLeave={handleDrageLeave}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}>
            <div
              className='list__item-link_ele'
              tabIndex={0}
              style={{ paddingLeft: 10 }}
              data-testid={`filter-list-item`}
              onClick={() => handleListFilterItemClick(item)}
              onKeyDown={(e) => {
                // Using <div> instead of <a> removes default Enter hehavior.
                if (e.key === 'Enter') {
                  handleListFilterItemClick(item);
                }
              }}>
              <div style={{ wordWrap: 'break-word' }}>
                {item.filterName} ({countActiveStores(item.ordcstmCdList, isAgency, groupAgencies)})
                {!item.isExist && (
                  <Icon color='error' style={{ marginRight: 5 }}>
                    error
                  </Icon>
                )}
              </div>
            </div>
            <Icon className='list__right-icon'>keyboard_arrow_right</Icon>
          </li>
        );
      })}
    </ul>
  );
};

interface StoreFilterTypeProps {
  isAgency: boolean;
}
const StoreFilterType = ({ isAgency }: StoreFilterTypeProps): React.ReactElement => {
  if (isAgency) return <StoreFilterAgencyContainer />;
  return <StoreFilterEmployee />;
};

interface ErrorFromStoreProps {
  isExist: boolean | undefined;
}

const ErrorFromStore = ({ isExist }: ErrorFromStoreProps): React.ReactElement => {
  if (isExist) return <></>;
  return (
    <Notice icon='error' type='error' isLong>
      <>参照権限がない店舗が削除されました。現在の選択店舗を確認し、フィルタを保存してください</>
    </Notice>
  );
};

interface ErrorFromClientProps {
  error: string;
}

const ErrorFromClient = ({ error }: ErrorFromClientProps) => {
  if (!error) return <></>;
  return (
    <Notice icon='error' type='error' isLong>
      {error}
    </Notice>
  );
};

export default StoreFilterManagement;
